import { useEffect, useMemo, useState } from "react";
import { staffAuth, getStaffCode, setStaffCode, clearStaffCode, setStaffRole, type StaffRole } from "../lib/api";
import { isLang, makeT, readStoredLang } from "../i18n/context";
import type { Lang } from "../i18n";
import { StaffRoleContext } from "./staffRoleContext";
import LogoTile from "./LogoTile";

// Access gate for operator-only screens (coach panel + TV leaderboard).
// Shows a code-entry screen until a valid staff code is stored on this device.
// The same code is enforced server-side, so the gate is real, not cosmetic.
export default function StaffGate({ children }: { children: React.ReactNode }) {
  const lang: Lang = useMemo(() => {
    const q = new URLSearchParams(window.location.search).get("lang");
    return isLang(q) ? q : readStoredLang();
  }, []);
  const t = useMemo(() => makeT(lang), [lang]);

  // "checking" while we verify an already-stored code on mount.
  const [status, setStatus] = useState<"checking" | "locked" | "ok">("checking");
  const [role, setRole] = useState<StaffRole | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    const stored = getStaffCode();
    if (!stored) {
      setStatus("locked");
      return;
    }
    staffAuth(stored)
      .then((r) => {
        if (!alive) return;
        if (r.role) {
          setStaffRole(r.role);
          setRole(r.role);
        }
        setStatus("ok");
      })
      .catch(() => {
        if (!alive) return;
        clearStaffCode();
        setStatus("locked");
      });
    return () => {
      alive = false;
    };
  }, []);

  async function submit() {
    setError("");
    const c = code.trim();
    if (!c) return setError(t("staff.error"));
    setBusy(true);
    try {
      const r = await staffAuth(c);
      setStaffCode(c);
      if (r.role) {
        setStaffRole(r.role);
        setRole(r.role);
      }
      setStatus("ok");
    } catch {
      setError(t("staff.error"));
    } finally {
      setBusy(false);
    }
  }

  if (status === "ok") return <StaffRoleContext.Provider value={role}>{children}</StaffRoleContext.Provider>;
  if (status === "checking") {
    return <div className="min-h-dvh bg-bg" />;
  }

  return (
    <div className="min-h-dvh bg-bg text-white flex flex-col items-center justify-center px-6">
      <LogoTile size={56} shadow="lg" />
      <h1 className="mt-6 text-2xl font-extrabold tracking-tight text-center">{t("staff.title")}</h1>
      <p className="text-muted text-sm mt-2 text-center max-w-xs leading-relaxed">{t("staff.subtitle")}</p>
      <div className="mt-6 w-full max-w-xs flex flex-col gap-3">
        <input
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder={t("staff.placeholder")}
          inputMode="text"
          className="w-full bg-input text-black rounded-[14px] px-4 py-3 text-center text-lg tracking-widest placeholder:text-grey/70 placeholder:tracking-normal outline-none focus:ring-2 focus:ring-violet"
        />
        {error && <p className="text-sm text-red-400 text-center">{error}</p>}
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="w-full h-14 rounded-[14px] bg-violet-grad text-white font-extrabold text-base active:opacity-90 transition-opacity disabled:opacity-60 shadow-violet-btn"
        >
          {busy ? t("staff.checking") : t("staff.submit")}
        </button>
      </div>
    </div>
  );
}
